<template>
  <pds-page-header>
    <template v-slot:title> Pomp onderhoud inplannen </template>
  </pds-page-header>
  <div class="pumpMaintenance -mt-32">
    <div class="mx-auto w-10/12 px-8 pb-12">
      <div class="rounded-lg px-8 py-6" v-if="smallestDate">
        <div v-for="(maintenancePump, index) in maintenancePumps" :key="index">
          <v-pump-maintenance-row
            :commonPumpMaintenanceDate="commonPumpMaintenanceDate"
            :ref="setPumpRowRef(index)"
            :data="maintenancePump"
            @update="updatePumpMaintenance"
            @remove="removePumpFromMaintenance"
            @get-disable-dates-for-postalcode="fetchDisableDatesForPostalCode"
          ></v-pump-maintenance-row>
        </div>
        <div class="row justify-end" v-if="maintenancePumps && maintenancePumps.length > 0">
          <v-button tagelement="a" md cta hollow transparent @click.prevent="$router.push('/pompenoverzicht')">
            Annuleren
          </v-button>
          <v-button md cta @click.prevent="savePumpMaintenance" :disabled="!checkButtonActiveStatus">
            Afronden
          </v-button>
        </div>
      </div>
    </div>
  </div>
  <pds-modal-dialog @update:is-active="handleUpdate" :modalOpenStatus="showTimeCheck">
    <template v-slot:title>Aanpassen ophaaldatum</template>
    <template v-slot:description>
      Wilt u deze datum toepassen van alle andere pompen in depot die moeten worden opgehaald voor onderhoud?
    </template>
  </pds-modal-dialog>
</template>

<script>
import { fetchQuery } from '@/services/messaging/fetchQuery';
import { activePpwRecId$ } from '@/services';
import { ref, getCurrentInstance, provide, computed, onBeforeMount } from 'vue';
import VPumpMaintenanceRow from './PumpMaintenanceRow';
import router from '@/router';
import store from '@/store';
import axios from 'axios';

export default {
  name: 'PumpMaintenance',
  components: { VPumpMaintenanceRow },

  setup() {
    const commonPumpMaintenanceDate = ref(null);
    const tempCommonMaintenanceDate = ref(null);
    const activePpwRecId = ref(null);
    activePpwRecId$.subscribe((value) => (activePpwRecId.value = value));
    const maintenancePumps = ref(null); // Reactive variable to store the API response
    const showTimeCheck = ref(false);
    const disableDatesForPickUp = ref(null);
    const smallestDate = ref(null);
    const differentPickupDates = ref(false);

    const checkButtonActiveStatus = computed(() => {
      return maintenancePumps.value.some((maintenancePump) => maintenancePump.customerId === null);
    });

    const getDisabledDatesForPumpsPickUp = (value) =>
      store.dispatch('pumpOverview/getUnSelectableDatesForPostalCode', value);

    // Array to store refs to pumpMaintenanceRow components
    const pumpMaintenanceRow = ref([]);

    // Assign the ref to the correct component instance
    const setPumpRowRef = (index) => (el) => {
      pumpMaintenanceRow.value[index] = el;
    };

    // Access global properties
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    // Utility function to get today's date as a string
    const getTodayDate = () => {
      const today = new Date();
      return today.toISOString().split('T')[0]; // Returns 'YYYY-MM-DD'
    };

    // Check if the modal was shown today
    const checkIfModalShownToday = () => {
      const lastShownDate = localStorage.getItem('modalShownPumpMaintenanceDate');
      const todayDate = getTodayDate();
      return lastShownDate !== todayDate;
    };

    // Fetch maintenance pumps data
    const fetchMaintenancePumps = async () => {
      try {
        const response = await fetchQuery('GetEquipmentRequireMaintenance', { ppwrecId: activePpwRecId.value });
        if (response && response.length > 0) {
          maintenancePumps.value = response; // Store response in the reactive variable
          if (response && response[0].defaultWarehouseAddress) {
            await fetchDisableDatesForPostalCode(response[0].defaultWarehouseAddress.postalCode);
          }
        }
      } catch (error) {
        console.error('Error fetching maintenance pumps:', error);
      }
    };

    const fetchDisableDatesForPostalCode = async (postalCode) => {
      if (postalCode && postalCode.length > 3) {
        let code = postalCode.split(' ')[0];
        if (code.length === 4) {
          let response = await getDisabledDatesForPumpsPickUp(code);
          if (response && response.data) disableDatesForPickUp.value = response.data.unSelectableDates;
          smallestDate.value = response.data.smallestDate;
          differentPickupDates.value = true;
        }
      }
    };

    const handleUpdate = (isActive) => {
      showTimeCheck.value = false;
      if (isActive) {
        commonPumpMaintenanceDate.value = tempCommonMaintenanceDate.value;
      }
    };

    const updatePumpMaintenance = (maintenanceDate) => {
      // Only trigger the modal if it hasn't been shown today
      if (!showTimeCheck.value && checkIfModalShownToday() && maintenancePumps.value.length > 1) {
        showTimeCheck.value = true;
        localStorage.setItem('modalShownPumpMaintenanceDate', getTodayDate());
        tempCommonMaintenanceDate.value = maintenanceDate;
      }
    };

    const removePumpFromMaintenance = (equipmentId) => {
      maintenancePumps.value = maintenancePumps.value.filter((pump) => pump.equipmentId !== equipmentId);
      if (maintenancePumps.value.length === 0) {
        router.push('/pompenoverzicht');
      }
    };

    const successNotification = () => {
      $emitter.emit('notification', {
        title: 'Onderhoudsafspraak succesvol ingepland',
        description:
          'Uw onderhoudsafspraak voor de pomp is succesvol ingepland. Op uw dashboard ziet u een overzicht van de onderhoudsafspraken die ingepland zijn.'
      });
      router.push('/pompenoverzicht');
    };

    provide('disableDatesForPickUp', disableDatesForPickUp);
    provide('smallestDate', smallestDate);
    provide('differentPickupDates', differentPickupDates);

    // Validate all fields for pump maintenance rows
    const savePumpMaintenance = async () => {
      let count = 0;
      let plannedPumpDeails = {
        equipments: []
      };
      for (let i = 0; i < maintenancePumps.value.length; i++) {
        const index = pumpMaintenanceRow.value.findIndex(
          (row) => row.data.equipmentId === maintenancePumps.value[i].equipmentId
        );
        let pumpDetails = await pumpMaintenanceRow.value[index].validateAllFields();
        if (!pumpDetails.status) {
          count++;
        }
        delete pumpDetails.status;
        if (pumpDetails.customerId === null) {
          delete pumpDetails.customerId;
          plannedPumpDeails.equipments.push(pumpDetails);
        }
      }
      if (count === 0) {
        axios.post('Equipment/PlanEquipmentMaintenance', plannedPumpDeails).then((response) => {
          if (response) {
            successNotification();
          }
        });
      }
    };

    // Fetch the data when the component is mounted
    onBeforeMount(() => {
      fetchMaintenancePumps();
    });

    return {
      checkButtonActiveStatus,
      commonPumpMaintenanceDate,
      activePpwRecId,
      maintenancePumps,
      showTimeCheck,
      successNotification,
      handleUpdate,
      updatePumpMaintenance,
      removePumpFromMaintenance,
      savePumpMaintenance,
      fetchDisableDatesForPostalCode,
      setPumpRowRef,
      smallestDate
    };
  }
};
</script>
